import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileSearch, AlertTriangle, Loader2, Copy, CheckCircle2 } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom renderers for the AI markdown output — declared outside the component
// so React doesn't recreate them on every render.
const MARKDOWN_COMPONENTS = {
  h1: (props) => (
    <h2
      className="text-lg font-bold uppercase tracking-wider mt-4 pt-3 border-t border-border/40 first:border-t-0 first:mt-0 first:pt-0"
      {...props}
    />
  ),
  h2: (props) => (
    <h3
      className="text-base font-bold uppercase tracking-wider mt-4 pt-3 border-t border-border/40 first:border-t-0 first:mt-0 first:pt-0"
      {...props}
    />
  ),
  h3: (props) => (
    <h4 className="text-sm font-semibold mt-3 text-muted-foreground" {...props} />
  ),
  p: (props) => <p className="text-sm leading-relaxed" {...props} />,
  ul: (props) => (
    <ul className="space-y-1.5 text-sm ml-1 list-none" {...props} />
  ),
  ol: (props) => <ol className="space-y-1.5 text-sm ml-5 list-decimal" {...props} />,
  li: ({ children, ...props }) => (
    <li
      className="pl-5 relative before:content-['▸'] before:absolute before:left-0 before:text-foreground/50"
      {...props}
    >
      {children}
    </li>
  ),
  strong: ({ children, ...props }) => {
    const raw = String(children);
    let cls = "font-semibold";
    if (/severity:\s*high/i.test(raw)) cls += " text-[#E07A5F]";
    else if (/severity:\s*medium/i.test(raw)) cls += " text-[#E07A5F]/80";
    else if (/severity:\s*low/i.test(raw)) cls += " text-[#81B29A]";
    return (
      <strong className={cls} {...props}>
        {children}
      </strong>
    );
  },
  code: ({ inline, ...props }) =>
    inline ? (
      <code className="px-1.5 py-0.5 rounded bg-secondary text-xs font-mono" {...props} />
    ) : (
      <code
        className="block p-3 rounded-sm bg-secondary text-xs font-mono overflow-x-auto"
        {...props}
      />
    ),
  hr: () => <hr className="border-border/50 my-4" />,
};

export const ContractScanner = () => {
  const [contractText, setContractText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const scanContract = async () => {
    if (!contractText.trim()) {
      toast.error("Please enter contract text");
      return;
    }

    if (contractText.length < 50) {
      toast.error("Please enter more contract text for accurate analysis");
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/ai/scan-contract`, {
        contract_text: contractText
      });
      setResult(response.data.result);
      toast.success("Contract analyzed");
    } catch (error) {
      toast.error("Analysis failed. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyResult = () => {
    navigator.clipboard.writeText(result);
    toast.success("Analysis copied to clipboard");
  };

  const exampleContract = `Event Venue Rental Agreement

Venue: Grand Ballroom, Downtown Convention Center
Date: December 15, 2025
Time: 6:00 PM - 11:00 PM (5 hours)
Base Rental Fee: $5,000

Additional Charges:
- Overtime: $500 per hour after 11:00 PM
- Setup fee: $750 (non-refundable)
- Cleaning deposit: $1,000 (refundable)
- Security personnel: $75/hour per guard (minimum 2 required)

Cancellation Policy:
- 90+ days: Full refund minus $500 administrative fee
- 60-89 days: 50% refund
- 30-59 days: 25% refund
- Less than 30 days: No refund

The venue reserves the right to relocate the event to an alternative space of similar size if necessary. Client is responsible for any damage to the venue or equipment. Insurance proof required 30 days prior to event.`;

  return (
    <Layout 
      title="Contract Scanner" 
      subtitle="AI analyses contracts to highlight hidden costs and red flags"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Input Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <FileSearch className="h-5 w-5 text-purple-400" />
              Contract Text
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="label-tactical">Paste Contract Text</Label>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setContractText(exampleContract)}
                  data-testid="load-example-button"
                >
                  Load Example
                </Button>
              </div>
              <Textarea
                placeholder="Paste your contract or venue agreement text here..."
                value={contractText}
                onChange={(e) => setContractText(e.target.value)}
                className="tactical-input min-h-[300px] font-mono text-sm"
                data-testid="contract-text-input"
              />
              <p className="text-xs text-muted-foreground">
                {contractText.length} characters • Minimum 50 characters recommended
              </p>
            </div>

            <Button 
              onClick={scanContract} 
              className="tactical-btn w-full bg-purple-600 hover:bg-purple-700"
              disabled={loading || contractText.length < 50}
              data-testid="scan-contract-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Analyzing Contract...
                </>
              ) : (
                <>
                  <FileSearch className="h-5 w-5 mr-2" />
                  Scan for Red Flags
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Card */}
        {result && (
          <Card className="tactical-card border-purple-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg uppercase text-purple-400">
                  <AlertTriangle className="h-5 w-5" />
                  Analysis Results
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={copyResult}
                  data-testid="copy-result-button"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div
                className="contract-scan-result space-y-3"
                data-testid="analysis-result"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                  {result}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <CheckCircle2 className="h-5 w-5 text-green-400" />
              What We Look For
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Hidden Costs</p>
                    <p className="text-xs text-muted-foreground">
                      Service fees, admin charges, mandatory add-ons
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Overtime Triggers</p>
                    <p className="text-xs text-muted-foreground">
                      Time penalties, early setup fees, late charges
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Cancellation Terms</p>
                    <p className="text-xs text-muted-foreground">
                      Refund policies, notice periods, force majeure
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-purple-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Liability Issues</p>
                    <p className="text-xs text-muted-foreground">
                      Damage responsibility, insurance requirements
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Payment Terms</p>
                    <p className="text-xs text-muted-foreground">
                      Deposit schedules, payment deadlines
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-pink-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Venue Rights</p>
                    <p className="text-xs text-muted-foreground">
                      Relocation clauses, exclusivity terms
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ContractScanner;
